import React, { ReactNode } from 'react'
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Dialog, { DialogProps } from '@material-ui/core/Dialog'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Typography from '@material-ui/core/Typography'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string
  children: ReactNode
  onClose: () => void
  dense?: boolean
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, dense, ...other } = props
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.root}
      style={dense ? { padding: '8px 48px 8px 16px' } : undefined}
      {...other}
    >
      <Typography variant={dense ? 'subtitle1' : 'h6'} noWrap={dense}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon data-testid="close-icon" />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
})

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent)

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions)

export interface CustomizedDialogsProps {
  isOpen: boolean
  onClose: () => void
  onOpen: () => void
  title?: string
  toggleText?: string
  content?: ReactNode
  actions?: ReactNode
  /** Widens the dialog past MUI's narrow default for content laid out in columns. */
  maxWidth?: DialogProps['maxWidth']
  /** Trims the title and padding so tall content fits a laptop screen without scrolling. */
  dense?: boolean
}

export default function DialogModal(props: CustomizedDialogsProps) {
  const { isOpen, toggleText, content, title, actions, onClose, onOpen, maxWidth, dense } = props

  return (
    <div>
      {toggleText && (
        <Button variant="outlined" color="primary" onClick={onOpen}>
          {toggleText}
        </Button>
      )}
      <Dialog
        onClose={onClose}
        aria-labelledby={title ? 'customized-dialog-title' : undefined}
        aria-label={title ? undefined : 'Preview'}
        open={isOpen}
        maxWidth={maxWidth}
        fullWidth={!!maxWidth}
        PaperProps={dense ? { style: { margin: 8, maxHeight: 'calc(100% - 16px)' } } : undefined}
      >
        {title ? (
          <DialogTitle id="customized-dialog-title" onClose={onClose} dense={dense}>
            {title}
          </DialogTitle>
        ) : (
          // With no title there is no bar to hang the close button on, so it floats over the
          // content — which is the point of dropping the title: the content gets that height.
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
          >
            <CloseIcon data-testid="close-icon" />
          </IconButton>
        )}
        <DialogContent dividers={!!title} style={dense ? { padding: 0 } : undefined}>
          {content}
        </DialogContent>
        {actions && <DialogActions>{actions}</DialogActions>}
      </Dialog>
    </div>
  )
}
